import React, { useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  FiAlertCircle,
  FiCheck,
  FiCopy,
  FiLink2,
  FiRefreshCw,
} from "react-icons/fi";
import AssistantMark from "./AssistantMark";
import markdownRenderers from "./markdownRenderers";
import { marketIcon } from "./markets";

const formatTime = (timestamp) => {
  try {
    return new Date(timestamp).toLocaleTimeString([], {
      hour: "numeric",
      minute: "2-digit",
    });
  } catch (error) {
    return "";
  }
};

/**
 * One turn in the transcript.
 *
 * User turns are a right-aligned tinted bubble; assistant turns are full-width
 * text with the identity mark at the left and no bubble. That asymmetry is
 * what makes a transcript scannable — you can tell who is speaking from the
 * shape alone, before reading a word. The old UI rendered both roles as one
 * flat block labelled "Question:" and "Response:".
 */
export default function MessageBubble({
  message,
  isStreaming = false,
  onRetry,
  onShare,
}) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(message.content);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch (error) {
      console.error("Could not copy message:", error);
    }
  };

  /* ---------------------------------------------------------------- user */
  if (message.role === "user") {
    const MarketIcon = message.region
      ? marketIcon(message.region, message.country)
      : null;

    return (
      <div className="flex animate-iq-rise flex-col items-end gap-1.5">
        <div className="max-w-[85%] rounded-2xl rounded-br-md bg-iq-bubble px-4 py-2.5 text-[15px] leading-[1.6] text-iq-ink">
          <p className="iq-user-text">{message.content}</p>
        </div>

        {/* The signature detail: every question carries the market it was
            asked against. Answers differ by market, so without this the
            transcript loses the one piece of context that explains why. */}
        {message.market && (
          <div className="flex items-center gap-1.5 pr-1 text-[11.5px] text-iq-ink-faint">
            {MarketIcon && <MarketIcon className="h-3 w-3" />}
            <span>{message.market}</span>
            {message.timestamp && (
              <>
                <span aria-hidden="true">·</span>
                <span>{formatTime(message.timestamp)}</span>
              </>
            )}
          </div>
        )}
      </div>
    );
  }

  /* ----------------------------------------------------------- assistant */
  const isError = message.status === "error";

  return (
    <div className="group flex animate-iq-rise gap-3">
      <AssistantMark busy={isStreaming} />

      <div className="min-w-0 flex-1">
        {isError ? (
          <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3">
            <div className="flex gap-2.5">
              <FiAlertCircle
                className="mt-0.5 h-4 w-4 shrink-0 text-red-500"
                aria-hidden="true"
              />
              <div className="min-w-0">
                <p className="text-[14px] leading-[1.6] text-red-800">
                  {message.content}
                </p>
                {onRetry && (
                  <button
                    type="button"
                    onClick={onRetry}
                    className="mt-2 inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-[12.5px] font-medium text-red-700 transition-colors hover:bg-red-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-400"
                  >
                    <FiRefreshCw className="h-3.5 w-3.5" />
                    Try again
                  </button>
                )}
              </div>
            </div>
          </div>
        ) : (
          <>
            <div
              className={`text-[15px] leading-[1.72] text-iq-ink ${
                isStreaming ? "iq-caret" : ""
              }`}
            >
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={markdownRenderers}
              >
                {message.content}
              </ReactMarkdown>
            </div>

            {message.status === "partial" && (
              <p className="mt-2 text-[12px] italic text-iq-ink-faint">
                This answer was cut short.
              </p>
            )}

            {/* Actions stay hidden until hover on pointer devices, but are
                always present for keyboard (focus-within) and touch (no
                hover), so they are never unreachable. */}
            {!isStreaming && (
              <div className="mt-2 flex items-center gap-0.5 transition-opacity sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100">
                <button
                  type="button"
                  onClick={handleCopy}
                  aria-label="Copy answer"
                  className="flex items-center gap-1.5 rounded-md px-2 py-1 text-[12px] text-iq-ink-faint transition-colors hover:bg-iq-rail hover:text-iq-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-iq-brand"
                >
                  {copied ? (
                    <FiCheck className="h-3.5 w-3.5" />
                  ) : (
                    <FiCopy className="h-3.5 w-3.5" />
                  )}
                  {copied ? "Copied" : "Copy"}
                </button>

                {onRetry && (
                  <button
                    type="button"
                    onClick={onRetry}
                    aria-label="Regenerate this answer"
                    className="flex items-center gap-1.5 rounded-md px-2 py-1 text-[12px] text-iq-ink-faint transition-colors hover:bg-iq-rail hover:text-iq-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-iq-brand"
                  >
                    <FiRefreshCw className="h-3.5 w-3.5" />
                    Retry
                  </button>
                )}

                {onShare && (
                  <button
                    type="button"
                    onClick={onShare}
                    aria-label="Copy a shareable link to this question"
                    className="flex items-center gap-1.5 rounded-md px-2 py-1 text-[12px] text-iq-ink-faint transition-colors hover:bg-iq-rail hover:text-iq-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-iq-brand"
                  >
                    <FiLink2 className="h-3.5 w-3.5" />
                    Share
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
